import React, { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { DataTable, type DataTableColumn } from "@/components/ui/DataTable/index";
import { useListCropSettings, type CropSetting } from "@/lib/api/risk/profiles/cropSettings";
import { SummaryFiltersValidation, useListRiskSummaryFull } from '@/lib/api/risk/summary';
import { formatNumberToDisplay, safeParseDecimal } from "@/lib/api/common";
import { get_groupable_commodity_display_name, Commodities } from "@/lib/api/commodity";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { CreateCropSettingDialog } from "./CreateCropSettingDialog";
import { UpdateCropSettingDialog } from "./UpdateCropSettingDialog";
import { useQueryParamArray } from "@/hooks/use-query-param-array";
import { RiskRevenueSummary } from "../Summary";
import { useQueryParam } from "@/hooks/use-query-param";
import { generateDisplayLabelForOtcDetailedType } from "../../common";

export function CropSettings({ className, baseTopLevelResult }: { className?: string; baseTopLevelResult: ReturnType<typeof useListRiskSummaryFull> }) {
  const { toast } = useToast();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [activeTab, handleTabChange] = useQueryParam("revenue_breakdown_detail", "sold");

  const [selectedSetting, setSelectedSetting] = useState<CropSetting | null>(null);
  const [customerIds, setCustomerIds] = useQueryParamArray("customer_ids");

  const cropSettingsResult = useListCropSettings({
    filters: {
      is_enabled: true
    }
  });

  const handleRowClick = (setting: CropSetting) => {
    setSelectedSetting(setting);
    setUpdateDialogOpen(true);
  };

  const handleCreateSuccess = () => {
    toast({ title: "Crop setting created successfully" });
    setCreateDialogOpen(false);
  };

  const handleUpdateSuccess = () => {
    toast({ title: "Crop setting updated successfully" });
    setUpdateDialogOpen(false);
    setSelectedSetting(null);
  };

  const summaryData = baseTopLevelResult?.data?.data?.summary;

  const { defaultTabSorting, tabColumns, tabData } = useMemo(() => {
    const defaultTabSorting = undefined;
    const tabColumns: DataTableColumn<any>[] = getRelevantDetailsColumns(activeTab);

    const brokerage_tabs = ["hedgeable_positions", "unhedgeable_positions", "p_and_s_positions"];
    const parent_group = brokerage_tabs.includes(activeTab || "") ? "brokerage" : activeTab;
    const tabData = (summaryData?.revenue_details[parent_group] || []).filter((pos: { is_hedgeable: boolean; trading_order_type: string }) => {
      if (!brokerage_tabs.includes(activeTab || "")) {
        return true;
      }

      if (pos.trading_order_type === 'p_and_s') {
        return "p_and_s_positions" === activeTab && pos.is_hedgeable;
      }


      if ("hedgeable_positions" === activeTab) {
        return pos.is_hedgeable ?? false;
      } else if ("unhedgeable_positions" === activeTab) {
        return !(pos.is_hedgeable ?? false);
      }

      return true;
    });

    return { defaultTabSorting, tabColumns, tabData };
  }, [activeTab, summaryData])

  const columns: DataTableColumn<CropSetting>[] = [
    {
      id: "commodity_name",
      accessorKey: "commodity_name",
      header: "Commodity",
      size: 180,
      cell: ({ row }) => row.original.commodity_name,
    },
    {
      id: "commodity_type",
      accessorKey: "commodity_type",
      header: "Commodity Type",
      size: 150,
      cell: ({ row }) => row.original.commodity_type,
    },
    {
      id: "option_month",
      accessorKey: "option_month",
      header: "Futures Month",
      size: 100,
      cell: ({ row }) => row.original.option_month,
    },
    {
      id: "customer_id",
      accessorKey: "customer_id",
      header: "Customer",
      size: 100,
      cell: ({ row }) => row.original.Customer?.long_name || "",
    },
    {
      id: "implied_basis",
      accessorKey: "implied_basis",
      header: "Implied Basis",
      size: 120,
      cell: ({ row }) => {
        if (!row.original.implied_basis) return '';
        return formatNumberToDisplay(row.original.implied_basis.toString(), {
          minimumFractionDigits: 2,
          maximumFractionDigits: 4
        });
      },
    },
    {
      id: "is_enabled",
      accessorKey: "is_enabled",
      header: "Enabled",
      size: 80,
      cell: ({ row }) => row.original.is_enabled ? "Yes" : "No",
    },
  ];

  const settings = cropSettingsResult?.data?.data || [];
  const isLoading = cropSettingsResult?.isLoading;
  const isError = !isLoading && (!!cropSettingsResult?.error || !cropSettingsResult?.data?.success);

  // Separate global settings (no customer_id and no option_month) from customer-specific settings
  const globalSettings = settings.filter((setting: CropSetting) => !setting.customer_id && !setting.option_month);
  const customerSettings = settings.filter((setting: CropSetting) => {
    if (customerIds.length > 0 && !!setting.customer_id && !customerIds.includes(setting.customer_id)) { return false; }
    return setting.customer_id || setting.option_month;
  });

  return (
    <div className={cn("space-y-4", className)}>
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Crop Settings</h2>
        <Button onClick={() => setCreateDialogOpen(true)} disabled={isLoading}>
          <Plus className="h-4 w-4 mr-1" />
          Create New Setting
        </Button>
      </div>

      {/* Main Layout - Side by side on desktop, stacked on mobile */}
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Left side - Global Basis Section (takes up more space) */}
        <div className="">
          {globalSettings.length > 0 && (
            <div className="">
              <h3 className="text-lg font-semibold text-gray-800">Global Basis</h3>
              {/* Optimized grid - more commodities per row, better utilization */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-2 sm:gap-3 auto-rows-min">
                {globalSettings.map((setting: CropSetting) => (
                  <div
                    key={setting.id}
                    onClick={() => handleRowClick(setting)}
                    className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm hover:shadow-md hover:border-gray-300 cursor-pointer transition-all duration-200 min-h-0"
                  >
                    <div className="space-y-2">
                      <div className="font-medium text-sm text-gray-900 truncate leading-tight" title={setting.commodity_name}>
                        {setting.commodity_name}
                      </div>
                      <div className="text-xs text-gray-600 truncate leading-tight" title={setting.commodity_type}>
                        {setting.commodity_type}
                      </div>
                      {setting.implied_basis && (
                        <div className="text-sm font-semibold text-blue-600 leading-tight">
                          {formatNumberToDisplay(setting.implied_basis.toString(), {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 4
                          })}
                        </div>
                      )}
                      <div className={`text-xs px-2 py-1 rounded-full w-fit leading-none ${setting.is_enabled 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-red-100 text-red-700'
                      }`}>
                        {setting.is_enabled ? 'Enabled' : 'Disabled'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Customer-Specific Overrides Table - moved under commodities on mobile */}
          <div className="space-y-3 mt-6">
            <h3 className="text-lg font-semibold text-gray-800">Specific Overrides</h3>
            <DataTable
              className="shadow-lg"
              tableClassName="overflow-y-auto"
              data={customerSettings}
              columns={columns}
              stickyHeaders
              enableColumnVisibility
              enableSorting
              enableMultiSort
              enableFiltering
              enableColumnResizing
              enableExport
              filterStyle='select'
              filterMode='internal'
              emptyMessage={isLoading ? "Loading..." : isError ? "Error loading crop settings" : "No specific overrides found"}
              defaultSorting={[
                { id: "crop_year", desc: true },
                { id: "commodity_name", desc: false },
                { id: "option_month", desc: false },
                { id: "customer", desc: false },
              ]}
              rowClickable
              onRowClick={handleRowClick}
            />
          </div>
        </div>

        {/* Right side - Revenue Summary (fixed width on desktop) */}
        <div className="md:min-w-[400px] flex-shrink-0">
          <RiskRevenueSummary 
            baseTopLevelResult={baseTopLevelResult} 
            className="h-full"
          />
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-800">Revenue Detailed Breakdown</h3>
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="">
              <TabsTrigger value="sold">Sold</TabsTrigger>
              <TabsTrigger value="unsold">Unsold</TabsTrigger>
              <TabsTrigger value="hedgeable_positions">Hedgeable Positions</TabsTrigger>
              <TabsTrigger value="unhedgeable_positions">Unhedgeable Positions</TabsTrigger>
              <TabsTrigger value="p_and_s_positions">PnS</TabsTrigger>
              <TabsTrigger value="unprotected">Unprotected</TabsTrigger>
              <TabsTrigger value="unprotected_unknown">Unknown Unprotected</TabsTrigger>
              <TabsTrigger value="unknown">Unknown</TabsTrigger>
          </TabsList>


          { ["sold", "unsold", "hedgeable_positions", "unhedgeable_positions", "p_and_s_positions", "unknown", "unprotected", "unprotected_unknown"].map((tabKey: string) => {
              return (
                  <TabsContent key={`RiskRevenueDetail_${tabKey}`} value={tabKey} className="space-y-4">
                    <DataTable
                      enableVirtualization
                    virtualizationRowHeight={49}
                    virtualizationOverscan={20}
                      tableClassName="overflow-y-auto"
                      data={tabData}
                      columns={tabColumns}
                      stickyHeaders
                      enableColumnVisibility
                      enableSorting
                      enableGrouping
                      enableColumnPinning
                      enableMultiSort
                      enableFiltering
                      enableColumnResizing
                      enableExport
                      filterStyle='select'
                      filterMode='internal'
                      emptyMessage={!baseTopLevelResult?.data?.data ? "Loading..." : isError ? "Error loading details" : "No details found"}
                      defaultSorting={defaultTabSorting}
                    />
                  </TabsContent>
              )
          })}
        </Tabs>

      </div>

      <CreateCropSettingDialog
        isOpen={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={handleCreateSuccess}
      />

      <UpdateCropSettingDialog
        isOpen={updateDialogOpen}
        onClose={() => {
          setUpdateDialogOpen(false);
          setSelectedSetting(null);
        }}
        onSuccess={handleUpdateSuccess}
        selectedSetting={selectedSetting}
      />
    </div>
  );
}

function getRelevantDetailsColumns(activeTab: string) {
  switch(activeTab) {
      case "sold":
      case "unsold":
      case "unknown":
      return getContractColumns();

      case "unprotected":
      case "unprotected_unknown":
        return getUnprotectedColumns();

      case "hedgeable_positions":
      case "unhedgeable_positions":
      case "p_and_s_positions":
        return getBrokerageColumns();

      default:
      return [];
  }
}

function getContractColumns(): DataTableColumn<any>[] {
  return [
      {
        id: "commodity_type",
        accessorKey: "commodity_type",
        header: "Commodity Type",
        cell: (info) => get_groupable_commodity_display_name(info.getValue() as string as Commodities),
      },
      {
        id: "crop_year",
        accessorKey: "crop_year",
        header: "Crop Year",
        aggregatedCell: () => "",
      },
      {
        id: "customer_id",
        accessorKey: "customer_id",
        accessorFn: (row) => row.Customer?.long_name || "",
        header: "Customer",
      },
      {
        id: "contract_type",
        accessorKey: 'contract_type',
        header: "Contract Type",
        enableColumnFilter: false,
        cell: (info) => {
          return (
            <span className="text-gray-600 text-sm">
              {generateDisplayLabelForOtcDetailedType(info.row.original.detailed_contract_type || info.row.original.contract_type)}
            </span>
          )
        },
      } as DataTableColumn<any>,
      {
          id: "option_month",
          accessorKey: "option_month",
          header: "Option Month",
      },
      {
          id: "quantity",
          accessorKey: "quantity",
          header: "Quantity",
        aggregatedCell: (info) => {
            return (
                <span className="text-gray-600 text-sm">
                    {formatNumberToDisplay(info.getValue() as string)}
                </span>
            )
        },
          cell: (info) => {
              return (
                  <span className="text-gray-600 text-sm">
                      {formatNumberToDisplay(info.getValue() as string)}
                  </span>
              )
          },
      } as DataTableColumn<any>,
      {
        id: "implied_cost",
        accessorKey: "implied_cost",
        header: "Revenue",
        accessorFn: (row) => safeParseDecimal({})(row.implied_cost || "0").toNumber(),
        aggregatedCell: (info) => {
            return (
                <span className="text-gray-600 text-sm">
                    {formatNumberToDisplay(info.getValue() as string)}
                </span>
            )
        },
        cell: (info) => {
            return (
                <span className="text-gray-600 text-sm">
                    {formatNumberToDisplay(info.getValue() as string)}
                </span>
            )
        },
      } as DataTableColumn<any>,
      {
        id: "futures",
        accessorKey: "futures",
        header: "Futures",
        cell: (info) => {
          const amount = /cnl|canola/im.test(info.row.original.commodity_type || "") ?
                         formatNumberToDisplay(info.getValue() as string, { minimumFractionDigits: 4, maximumFractionDigits: 4 }) :
                         formatNumberToDisplay(info.getValue() as string);


            return (
                <span className="text-gray-600 text-sm">
                    {amount}
                </span>
            )
        },
      },
      {
        id: "basis",
        accessorKey: "basis",
        header: "Basis",
        cell: (info) => {
            return (
                <span className="text-gray-600 text-sm">
                    {formatNumberToDisplay(info.getValue() as string)}
                </span>
            )
        },
      },
      {
        id: "implied_futures",
        accessorKey: "implied_futures",
        header: "Implied Futures",
        cell: (info) => {
          const amount = /cnl|canola/im.test(info.row.original.commodity_type || "") ?
                         formatNumberToDisplay(info.getValue() as string, { minimumFractionDigits: 4, maximumFractionDigits: 4 }) :
                         formatNumberToDisplay(info.getValue() as string);


            return (
                <span className="text-gray-600 text-sm">
                    {amount}
                </span>
            )
        }
      },
      {
        id: "implied_futures_contract",
        accessorKey: "implied_futures_contract",
        header: "Implied Futures Contract",
      },
      {
        id: "implied_basis",
        accessorKey: "implied_basis",
        header: "Implied Basis",
        cell: (info) => {
            return (
                <span className="text-gray-600 text-sm">
                    {formatNumberToDisplay(info.getValue() as string)}
                </span>
            )
        },
      },

      {
        id: "implied_fx_futures",
        accessorKey: "implied_fx_futures",
        header: "Implied FX Futures",
        cell: (info) => {
            return (
                <span className="text-gray-600 text-sm">
                    {formatNumberToDisplay(info.getValue() as string || "0", { maximumFractionDigits: 4 })}
                </span>
            )
        },
      },
      {
        id: "implied_fx_futures_contract",
        accessorKey: "implied_fx_futures_contract",
        header: "Implied FX Futures Contract",
        cell: (info) => {
            return (
                <span className="text-gray-600 text-sm">
                    {info.getValue() as string || "--"}
                </span>
            )
        },
      },
      {
          id: "contract_number",
          accessorKey: "contract_number",
          header: "Contract Number",
      },
    ];
}

function getBrokerageColumns(): DataTableColumn<any>[] {
  return [
      {
        id: "commodity_type",
        accessorKey: "commodity_type",
        header: "Commodity Type",
        cell: (info) => get_groupable_commodity_display_name(info.getValue() as string as Commodities),
      },
      {
        id: "crop_year",
        accessorKey: "crop_year",
        header: "Crop Year",
        aggregatedCell: () => "",
      },
      {
        id: "customer_id",
        accessorKey: "customer_id",
        accessorFn: (row) => row.Customer?.long_name || "",
        header: "Customer",
      },
      {
          id: "option_month",
          accessorKey: "option_month",
          header: "Option Month",
      },
      {
          id: "quantity",
          accessorKey: "quantity",
          header: "Quantity",
        aggregatedCell: (info) => {
            return (
                <span className="text-gray-600 text-sm">
                    {formatNumberToDisplay(info.getValue() as string)}
                </span>
            )
        },
          cell: (info) => {
              return (
                  <span className="text-gray-600 text-sm">
                      {formatNumberToDisplay(info.getValue() as string)}
                  </span>
              )
          },
      } as DataTableColumn<any>,
      {
        id: "implied_cost",
        accessorKey: "implied_cost",
        header: "Revenue",
        accessorFn: (row) => safeParseDecimal({})(row.implied_cost || "0").toNumber(),
        aggregatedCell: (info) => {
            return (
                <span className="text-gray-600 text-sm">
                    {formatNumberToDisplay(info.getValue() as string)}
                </span>
            )
        },
        cell: (info) => {
            return (
                <span className="text-gray-600 text-sm">
                    {formatNumberToDisplay(info.getValue() as string)}
                </span>
            )
        },
      } as DataTableColumn<any>,
      /* {
*   id: "futures",
*   accessorKey: "futures",
*   header: "Futures",
*   cell: (info) => {
*       return (
*           <span className="text-gray-600 text-sm">
*               {formatNumberToDisplay(info.getValue() as string)}
*           </span>
*       )
*   },
* },
* {
*   id: "basis",
*   accessorKey: "basis",
*   header: "Basis",
*   cell: (info) => {
*       return (
*           <span className="text-gray-600 text-sm">
*               {formatNumberToDisplay(info.getValue() as string)}
*           </span>
*       )
*   },
* }, */
      {
        id: "implied_futures",
        accessorKey: "implied_futures",
        header: "Implied Futures",
        cell: (info) => {
          const amount = /cnl|canola/im.test(info.row.original.commodity_type || "") ?
                         formatNumberToDisplay(info.getValue() as string, { minimumFractionDigits: 4, maximumFractionDigits: 4 }) :
                         formatNumberToDisplay(info.getValue() as string);


            return (
                <span className="text-gray-600 text-sm">
                    {amount}
                </span>
            )
        },
      },
      {
        id: "close_price",
        accessorKey: "close_price",
        header: "Close",
        cell: (info) => {
            return (
                <span className="text-gray-600 text-sm">
                    {formatNumberToDisplay(info.getValue() as string)}
                </span>
            )
        },
      },
      {
        id: "price",
        accessorKey: "price",
        header: "Price",
        cell: (info) => {
            return (
                <span className="text-gray-600 text-sm">
                    {formatNumberToDisplay(info.getValue() as string)}
                </span>
            )
        },
      },
      /* {
*   id: "implied_futures_contract",
*   accessorKey: "implied_futures_contract",
*   header: "Implied Futures Contract",
* }, */
      /* {
*   id: "implied_basis",
*   accessorKey: "implied_basis",
*   header: "Implied Basis",
*   cell: (info) => {
*       return (
*           <span className="text-gray-600 text-sm">
*               {formatNumberToDisplay(info.getValue() as string)}
*           </span>
*       )
*   },
* }, */
      {
          id: "contract_description",
          accessorKey: "contract_description",
          header: "Contract",
      },
      /* {
*   id: "implied_fx_futures",
*   accessorKey: "implied_fx_futures",
*   header: "Implied FX Futures",
*   cell: (info) => {
*       return (
*           <span className="text-gray-600 text-sm">
*               {formatNumberToDisplay(info.getValue() as string || "0", { maximumFractionDigits: 4 })}
*           </span>
*       )
*   },
* }, */
      /* {
*   id: "implied_fx_futures_contract",
*   accessorKey: "implied_fx_futures_contract",
*   header: "Implied FX Futures Contract",
*   cell: (info) => {
*       return (
*           <span className="text-gray-600 text-sm">
*               {info.getValue() as string || "--"}
*           </span>
*       )
*   },
* }, */
      /* {
*     id: "contract_number",
*     accessorKey: "contract_number",
*     header: "Contract #",
* }, */
    ];
}

function getUnprotectedColumns(): DataTableColumn<any>[] {
  return [
      {
        id: "commodity_type",
        accessorKey: "commodity_type",
        header: "Commodity Type",
        cell: (info) => get_groupable_commodity_display_name(info.getValue() as string as Commodities),
      },
      {
        id: "crop_year",
        accessorKey: "crop_year",
        header: "Crop Year",
        aggregatedCell: () => "",
      },
      {
        id: "customer_id",
        accessorKey: "customer_id",
        accessorFn: (row) => row.Customer?.long_name || "",
        header: "Customer",
      } as DataTableColumn<any>,
      {
          id: "quantity",
          accessorKey: "quantity",
          header: "Quantity",
        aggregatedCell: (info) => {
            return (
                <span className="text-gray-600 text-sm">
                    {formatNumberToDisplay(info.getValue() as string)}
                </span>
            )
        },
          cell: (info) => {
              return (
                  <span className="text-gray-600 text-sm">
                      {formatNumberToDisplay(info.getValue() as string)}
                  </span>
              )
          },
      } as DataTableColumn<any>,
      {
        id: "implied_cost",
        accessorKey: "implied_cost",
        header: "Revenue",
        accessorFn: (row) => safeParseDecimal({})(row.implied_cost || "0").toNumber(),
        aggregatedCell: (info) => {
            return (
                <span className="text-gray-600 text-sm">
                    {formatNumberToDisplay(info.getValue() as string)}
                </span>
            )
        },
        cell: (info) => {
            return (
                <span className="text-gray-600 text-sm">
                    {formatNumberToDisplay(info.getValue() as string)}
                </span>
            )
        },
      } as DataTableColumn<any>,
      {
        id: "implied_futures",
        accessorKey: "implied_futures",
        header: "Implied Futures",
        cell: (info) => {
          const amount = /cnl|canola/im.test(info.row.original.commodity_type || "") ?
                         formatNumberToDisplay(info.getValue() as string, { minimumFractionDigits: 4, maximumFractionDigits: 4 }) :
                         formatNumberToDisplay(info.getValue() as string);


            return (
                <span className="text-gray-600 text-sm">
                    {formatNumberToDisplay(info.getValue() as string)}
                </span>
            )
        },
      },
      {
        id: "implied_futures_contract",
        accessorKey: "implied_futures_contract",
        header: "Implied Futures Contract",
      },
      {
        id: "implied_basis",
        accessorKey: "implied_basis",
        header: "Implied Basis",
        cell: (info) => {
            return (
                <span className="text-gray-600 text-sm">
                    {formatNumberToDisplay(info.getValue() as string)}
                </span>
            )
        },
      },

      {
        id: "implied_fx_futures",
        accessorKey: "implied_fx_futures",
        header: "Implied FX Futures",
        cell: (info) => {
            return (
                <span className="text-gray-600 text-sm">
                    {formatNumberToDisplay(info.getValue() as string || "0", { maximumFractionDigits: 4 })}
                </span>
            )
        },
      },
      {
        id: "implied_fx_futures_contract",
        accessorKey: "implied_fx_futures_contract",
        header: "Implied FX Futures Contract",
        cell: (info) => {
            return (
                <span className="text-gray-600 text-sm">
                    {info.getValue() as string || "--"}
                </span>
            )
        },
      },
    ];
}
